import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Undo2, Truck, RotateCcw, Loader2, Package, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const GatePassCard = ({ entry, mode, onAction, busy }) => (
  <div
    className="bg-white rounded-lg border border-[#E5DFD0] p-4 flex flex-col gap-2"
    data-testid={`gatepass-card-${entry.gate_pass_number}`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="font-bold text-[#1A1F2E] font-mono">{entry.gate_pass_number}</span>
      <Badge variant={mode === 'returnable' ? 'default' : 'destructive'}>
        {mode === 'returnable' ? 'Dispatched' : 'Returned'}
      </Badge>
    </div>
    <div className="text-sm text-[#5A5F6E] space-y-1">
      <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5" /> {entry.customer || '-'}</div>
      <div>DC: <span className="font-medium text-[#1A1F2E]">{(entry.dc_numbers && entry.dc_numbers.length) ? entry.dc_numbers.join(', ') : '—'}</span></div>
      <div>Boxes: {entry.total_boxes || 0} · Cartons: {entry.total_cartons || 0} · Qty: {entry.total_qty || 0}</div>
      <div>Vehicle: {entry.vehicle_number || '-'} · {entry.transport_name || '-'}</div>
      <div>Dispatched: {fmtDate(entry.created_at)}</div>
      {mode === 'returned' && (
        <>
          <div className="text-rose-700">Returned: {fmtDate(entry.returned_at)} by {entry.returned_by || '-'}</div>
          <div className="text-rose-700">Reason: {entry.return_reason || '-'}</div>
        </>
      )}
    </div>
    <div className="pt-1">
      {mode === 'returnable' ? (
        <Button size="sm" variant="destructive" disabled={busy}
          onClick={() => onAction(entry)}
          data-testid={`record-return-btn-${entry.gate_pass_number}`}>
          <Undo2 className="w-4 h-4 mr-1" /> Record Return
        </Button>
      ) : (
        <Button size="sm" disabled={busy}
          onClick={() => onAction(entry)}
          data-testid={`redispatch-btn-${entry.gate_pass_number}`}>
          <RotateCcw className="w-4 h-4 mr-1" /> Re-dispatch (same DC)
        </Button>
      )}
    </div>
  </div>
);

const UnitCard = ({ u }) => (
  <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 flex flex-col gap-2"
    data-testid={`unit-card-${u.code}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-bold text-[#1A1F2E] font-mono">{u.code}</span>
      <Badge variant="secondary">{u.unit_type === 'carton' ? 'Carton' : 'Box'}</Badge>
    </div>
    <div className="text-sm text-[#5A5F6E] space-y-1">
      <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5" /> {u.customer || '-'}</div>
      <div>DC: <span className="font-medium text-[#1A1F2E]">{(u.dc_numbers && u.dc_numbers.length) ? u.dc_numbers.join(', ') : '—'}</span> · From GP: {u.original_gate_pass || '-'}</div>
      <div>Boxes: {u.total_boxes || 0} · Qty: {u.total_qty || 0}</div>
      <div className="text-rose-700">Returned: {fmtDate(u.returned_at)} by {u.returned_by || '-'}</div>
      <div className="text-rose-700">Reason: {u.return_reason || '-'}</div>
    </div>
    <div className="text-xs text-[#8A6D3B] bg-[#FBF3E2] rounded p-2">
      Scan this {u.unit_type === 'carton' ? 'carton' : 'box'} in Customer Outward to ship it again on a new gate pass (same DC).
    </div>
  </div>
);

const TransporterReturns = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('returnable');
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [dialogType, setDialogType] = useState(null); // 'return' | 'redispatch'
  // Single box/carton return
  const [scanCode, setScanCode] = useState('');
  const [scanUnit, setScanUnit] = useState(null);
  const [scanError, setScanError] = useState('');
  const [scanBusy, setScanBusy] = useState(false);
  const [boxReason, setBoxReason] = useState('');
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const load = useCallback(async (m, s) => {
    setLoading(true);
    try {
      const r = await apiClient.get('/customer-outward/returns/list', { params: { mode: m, search: s || undefined } });
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load gate passes');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnits = useCallback(async (s) => {
    setUnitsLoading(true);
    try {
      const r = await apiClient.get('/customer-outward/returns/units', { params: { status: 'returned', search: s || undefined } });
      setUnits(r.data?.units || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load returned units');
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearch('');
    if (mode === 'returnable' || mode === 'returned') load(mode, '');
    else if (mode === 'box-worklist') loadUnits('');
  }, [mode, load, loadUnits]);

  const openReturn = (entry) => { setSelected(entry); setReason(''); setDialogType('return'); };
  const openRedispatch = (entry) => { setSelected(entry); setDialogType('redispatch'); };
  const closeDialog = () => { setDialogType(null); setSelected(null); setReason(''); };

  const confirmReturn = async () => {
    if (!reason.trim()) { toast.error('Return reason is required'); return; }
    setBusy(true);
    try {
      const r = await apiClient.post('/customer-outward/returns/record', {
        entry_id: selected.entry_id, return_reason: reason.trim(),
      });
      toast.success(r.data?.message || 'Return recorded');
      closeDialog();
      load(mode, search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record return');
    } finally { setBusy(false); }
  };

  const confirmRedispatch = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post('/customer-outward/returns/redispatch', { entry_id: selected.entry_id });
      toast.success(r.data?.message || 'Re-dispatched');
      closeDialog();
      load(mode, search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-dispatch');
    } finally { setBusy(false); }
  };

  const lookupUnit = async () => {
    const code = scanCode.trim();
    if (!code) return;
    setScanBusy(true); setScanError(''); setScanUnit(null);
    try {
      const r = await apiClient.get('/customer-outward/returns/scan-unit', { params: { code } });
      if (r.data?.valid) { setScanUnit(r.data.unit); setBoxReason(''); }
      else setScanError(r.data?.message || 'Box / carton not found');
    } catch (e) {
      setScanError(e.response?.data?.detail || 'Lookup failed');
    } finally { setScanBusy(false); }
  };

  const recordUnitReturn = async () => {
    if (!boxReason.trim()) { toast.error('Return reason is required'); return; }
    setScanBusy(true);
    try {
      const r = await apiClient.post('/customer-outward/returns/record-unit', {
        code: scanUnit.code, return_reason: boxReason.trim(),
      });
      toast.success(r.data?.message || 'Return recorded');
      setScanUnit(null); setScanCode(''); setBoxReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record return');
    } finally { setScanBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-[#F9FAFB] hover:bg-white/10"
            onClick={() => navigate('/admin/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Truck className="w-7 h-7" />
          <div>
            <h1 className="text-xl font-bold font-display">Transporter Returns</h1>
            <p className="text-sm text-[#E8DCC8]">Return a whole gate pass or a single box / carton · re-dispatch on a new gate pass</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="mb-6 flex-wrap h-auto" data-testid="returns-tabs">
            <TabsTrigger value="returnable" data-testid="tab-record-return">Return Gate Pass</TabsTrigger>
            <TabsTrigger value="returned" data-testid="tab-redispatch">Re-dispatch Gate Pass</TabsTrigger>
            <TabsTrigger value="box-return" data-testid="tab-box-return">Return Box / Carton</TabsTrigger>
            <TabsTrigger value="box-worklist" data-testid="tab-box-worklist">Awaiting Re-dispatch</TabsTrigger>
          </TabsList>

          {/* ---- Whole gate-pass return / re-dispatch ---- */}
          {(mode === 'returnable' || mode === 'returned') && (
            <>
              <div className="flex gap-2 mb-6 max-w-md">
                <Input
                  placeholder="Search gate pass, DC, customer or vehicle…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load(mode, search)}
                  data-testid="returns-search-input"
                />
                <Button variant="outline" onClick={() => load(mode, search)} data-testid="returns-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <TabsContent value={mode} className="mt-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16 text-[#5A5F6E]">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
                  </div>
                ) : entries.length === 0 ? (
                  <div className="bg-white rounded-lg border border-[#E5DFD0] p-10 text-center text-[#5A5F6E]"
                    data-testid="returns-empty-state">
                    {mode === 'returnable'
                      ? 'No dispatched gate passes found.'
                      : 'No returned gate passes awaiting re-dispatch.'}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="returns-list">
                    {entries.map((e) => (
                      <GatePassCard
                        key={e.entry_id}
                        entry={e}
                        mode={mode}
                        busy={busy}
                        onAction={mode === 'returnable' ? openReturn : openRedispatch}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </>
          )}

          {/* ---- Return a single box / carton by scan ---- */}
          {mode === 'box-return' && (
            <TabsContent value="box-return" className="mt-0">
              <div className="max-w-xl space-y-4">
                <p className="text-sm text-[#5A5F6E]">
                  Scan a <b>box number</b> or a <b>master carton number</b> that was already dispatched.
                  It will be held as returned (same DC) and can be shipped again on a new gate pass via Customer Outward.
                </p>
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    placeholder="Scan box number or master carton no…"
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupUnit()}
                    data-testid="unit-scan-input"
                  />
                  <Button onClick={lookupUnit} disabled={scanBusy} data-testid="unit-scan-btn">
                    {scanBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                  </Button>
                </div>

                {scanError && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700"
                    data-testid="unit-scan-error">{scanError}</div>
                )}

                {scanUnit && (
                  <div className="bg-white border border-[#E5DFD0] rounded-lg p-4 space-y-2" data-testid="unit-scan-result">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#1A1F2E] font-mono">{scanUnit.code}</span>
                      <Badge>{scanUnit.unit_type === 'carton' ? 'Master Carton' : 'Box'}</Badge>
                    </div>
                    <div className="text-sm text-[#5A5F6E] space-y-1">
                      <div>Customer: <b className="text-[#1A1F2E]">{scanUnit.customer || '-'}</b></div>
                      <div>Original Gate Pass: <b className="text-[#1A1F2E]">{scanUnit.original_gate_pass || '-'}</b></div>
                      <div>DC: <b className="text-[#1A1F2E]">{(scanUnit.dc_numbers || []).join(', ') || '—'}</b></div>
                      <div>Boxes: {scanUnit.total_boxes || 0} · Qty: {scanUnit.total_qty || 0}</div>
                      {scanUnit.unit_type === 'carton' && (
                        <div>Box numbers: {(scanUnit.box_numbers || []).join(', ')}</div>
                      )}
                    </div>
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="unit-reason">Return Reason <span className="text-rose-600">*</span></Label>
                      <Textarea id="unit-reason" rows={2} value={boxReason}
                        onChange={(e) => setBoxReason(e.target.value)}
                        placeholder="e.g. wrong box loaded / customer refused this carton"
                        data-testid="unit-return-reason" />
                      <p className="text-xs text-[#5A5F6E]">
                        Stays on the SAME DC. After returning, scan it in Customer Outward to ship again on a new gate pass with any transport.
                      </p>
                    </div>
                    <Button variant="destructive" onClick={recordUnitReturn} disabled={scanBusy}
                      data-testid="unit-return-confirm">
                      {scanBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}
                      Record Return
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* ---- Worklist: units awaiting re-dispatch ---- */}
          {mode === 'box-worklist' && (
            <TabsContent value="box-worklist" className="mt-0">
              <div className="flex gap-2 mb-6 max-w-2xl">
                <Input
                  placeholder="Search box, carton, gate pass, DC or customer…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadUnits(search)}
                  data-testid="units-search-input"
                />
                <Button variant="outline" onClick={() => loadUnits(search)} data-testid="units-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
                <Button onClick={() => navigate('/gate-entry/outward-customer')} data-testid="go-outward-btn">
                  <Truck className="w-4 h-4 mr-1" /> Customer Outward
                </Button>
              </div>
              {unitsLoading ? (
                <div className="flex items-center justify-center py-16 text-[#5A5F6E]">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
                </div>
              ) : units.length === 0 ? (
                <div className="bg-white rounded-lg border border-[#E5DFD0] p-10 text-center text-[#5A5F6E]"
                  data-testid="units-empty-state">
                  No boxes or cartons awaiting re-dispatch.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="units-list">
                  {units.map((u) => <UnitCard key={u.return_id} u={u} />)}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Record Return dialog */}
      <Dialog open={dialogType === 'return'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>Record Transporter Return</DialogTitle>
            <DialogDescription>
              Gate Pass <b>{selected?.gate_pass_number}</b> · DC{' '}
              <b>{(selected?.dc_numbers || []).join(', ') || '—'}</b> · {selected?.customer}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="return-reason">Return Reason <span className="text-rose-600">*</span></Label>
            <Textarea id="return-reason" rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer refused delivery / vehicle breakdown returned"
              data-testid="return-reason-input" />
            <p className="text-xs text-[#5A5F6E]">
              Goods are held as “returned” (not re-picked) and stay assigned to this DC. You can re-dispatch later under the same DC.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={busy} data-testid="return-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={confirmReturn} disabled={busy} data-testid="return-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}
              Confirm Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-dispatch dialog */}
      <Dialog open={dialogType === 'redispatch'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="redispatch-dialog">
          <DialogHeader>
            <DialogTitle>Re-dispatch (same DC)</DialogTitle>
            <DialogDescription>
              A new Gate Pass will be generated under the SAME DC{' '}
              <b>{(selected?.dc_numbers || []).join(', ') || '—'}</b> for{' '}
              <b>{selected?.customer}</b>. Goods are not re-picked.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-[#5A5F6E]">
            Original Gate Pass: <b>{selected?.gate_pass_number}</b> · Boxes {selected?.total_boxes || 0} · Qty {selected?.total_qty || 0}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={busy} data-testid="redispatch-cancel-btn">Cancel</Button>
            <Button onClick={confirmRedispatch} disabled={busy} data-testid="redispatch-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Confirm Re-dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransporterReturns;
